import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Printer } from "lucide-react";
import { fetchMenus } from "../lib/api";
import { tr } from "../lib/i18n";

/* Printable sheet of per-position QR codes: /qr-posti?menu=<id>&n=30&label=Lettino&from=1 */
export default function QrPositionsPage() {
  const [qs, setQs] = useSearchParams();
  const menuId = qs.get("menu") || "";
  const n = Math.min(200, Math.max(1, parseInt(qs.get("n") || "30", 10) || 30));
  const from = Math.max(1, parseInt(qs.get("from") || "1", 10) || 1);
  const label = qs.get("label") || "Lettino";
  const [menus, setMenus] = useState([]);
  useEffect(() => { fetchMenus().then(setMenus).catch(() => {}); }, []);
  const menu = menus.find((m) => m.id === menuId);
  const base = window.location.origin;
  const positions = useMemo(() => Array.from({ length: n }, (_, i) => from + i), [n, from]);
  const set = (k, v) => { const p = new URLSearchParams(qs); p.set(k, v); setQs(p, { replace: true }); };

  return (
    <div className="min-h-screen bg-[#faf3ee] text-[#2b1410]" data-testid="qr-positions-page">
      <div className="print:hidden sticky top-0 z-10 bg-white border-b border-[#e8dcc8] px-4 py-3">
        <div className="max-w-5xl mx-auto flex flex-wrap items-end gap-3">
          <label className="text-xs text-[#7c5a4e]">Menu
            <select value={menuId} onChange={(e) => set("menu", e.target.value)} className="block mt-1 rounded-md border border-[#e8dcc8] px-2 py-1.5 text-sm bg-white min-w-48" data-testid="qr-pos-menu">
              <option value="">— scegli —</option>
              {menus.map((m) => <option key={m.id} value={m.id}>{tr(m.name, "it")}{m.wa_order_enabled ? " · WhatsApp" : ""}</option>)}
            </select>
          </label>
          <label className="text-xs text-[#7c5a4e]">Etichetta
            <input value={label} onChange={(e) => set("label", e.target.value)} className="block mt-1 rounded-md border border-[#e8dcc8] px-2 py-1.5 text-sm w-32" data-testid="qr-pos-label" />
          </label>
          <label className="text-xs text-[#7c5a4e]">Da n°
            <input type="number" min={1} value={from} onChange={(e) => set("from", e.target.value)} className="block mt-1 rounded-md border border-[#e8dcc8] px-2 py-1.5 text-sm w-20" data-testid="qr-pos-from" />
          </label>
          <label className="text-xs text-[#7c5a4e]">Quanti
            <input type="number" min={1} max={200} value={n} onChange={(e) => set("n", e.target.value)} className="block mt-1 rounded-md border border-[#e8dcc8] px-2 py-1.5 text-sm w-20" data-testid="qr-pos-n" />
          </label>
          <button onClick={() => window.print()} disabled={!menu} className="ml-auto inline-flex items-center gap-2 rounded-full bg-[#4a1c17] text-[#f7ebdf] px-4 py-2 text-sm font-semibold disabled:opacity-40" data-testid="qr-pos-print">
            <Printer className="h-4 w-4" /> Stampa / PDF
          </button>
        </div>
        {menu && !menu.wa_order_enabled && (
          <p className="max-w-5xl mx-auto mt-2 text-xs text-amber-800">Questo menu non ha "Ordine via WhatsApp" attivo: i QR aprono il menu, ma senza il pulsante Invia ordine.</p>
        )}
      </div>

      {menu ? (
        <div className="max-w-5xl mx-auto p-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 print:grid-cols-4 print:gap-2" data-testid="qr-pos-grid">
          {positions.map((p) => {
            const place = `${label} ${p}`;
            const url = `${base}/menu/${menu.id}?posto=${encodeURIComponent(place)}`;
            return (
              <div key={p} className="bg-white rounded-2xl border border-[#e8dcc8] p-3 flex flex-col items-center text-center break-inside-avoid print:rounded-none print:border-[#2b1410]" data-testid={`qr-pos-card-${p}`}>
                <div className="text-[10px] uppercase tracking-widest text-[#7c5a4e]">{tr(menu.name, "it")}</div>
                <div className="font-serif text-2xl font-bold leading-tight my-1">{place}</div>
                <QRCodeSVG value={url} size={128} fgColor="#2b1410" bgColor="#ffffff" level="M" />
                <div className="mt-2 text-[10px] text-[#7c5a4e]">Scansiona · ordina · ti raggiungiamo qui</div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="max-w-5xl mx-auto p-6 text-sm text-[#7c5a4e]">Scegli il menu per generare i QR delle postazioni.</p>
      )}
      <style>{`@media print { @page { margin: 8mm; } body { background: #fff; } }`}</style>
    </div>
  );
}
